import { useState, useEffect, useCallback, useMemo } from 'react'
import { useDiscogsConnection, useSyncStatus } from '../context/DiscogsContext'
import { useToast } from '../context/ToastContext'
import { useViewSort } from '../context/ViewSortContext'
import { useReleases, usePageTabs, useWantlist } from '../hooks'
import { ReleaseFilters as ReleaseFiltersType } from '../types/release'
import { LoadingState, Pagination, ReleaseToolbar, Button, PageTabBar } from '../components/common'
import { DiscogsSyncBadge } from '../components/DiscogsWriteGate'
import { ReleaseFilters } from '../components/releases/ReleaseFilters'
import { ReleaseListView } from '../components/releases/ReleaseListView'
import styles from './PageStyles.module.css'
import releaseStyles from '../components/releases/Releases.module.css'

const DEFAULT_FILTERS: ReleaseFiltersType = { page: 1, per_page: 50, sort: 'bayesian_rating', order: 'desc' }
const SORT_DEFAULT_ASC = new Set(['title', 'released'])

export function WantlistPage() {
  const { user, isAuthenticated, isLoggingIn, login, fetchWantlist } = useDiscogsConnection()
  const { syncProgress } = useSyncStatus()
  const { ids: wantlistIds, dates: wantlistDates } = useWantlist()
  const { showToast } = useToast()
  const { viewMode, sort: globalSort, order: globalOrder } = useViewSort()

  const [showFilters, setShowFilters] = useState(false)
  const [search, setSearch] = useState(() => {
    try { const s = sessionStorage.getItem('stonks_wantlist_filters'); if (s) { return JSON.parse(s).q || '' } } catch {} return ''
  })

  const isSyncing = syncProgress.type === 'wantlist'

  const [filters, setFilters] = useState<ReleaseFiltersType>(() => {
    try {
      const saved = sessionStorage.getItem('stonks_wantlist_filters')
      if (saved) return JSON.parse(saved)
    } catch { /* ignore */ }
    return { ...DEFAULT_FILTERS, sort: globalSort, order: globalOrder }
  })

  // Persist filters to sessionStorage
  useEffect(() => {
    try { sessionStorage.setItem('stonks_wantlist_filters', JSON.stringify(filters)) } catch {}
  }, [filters])

  const { savedSearches: tabs, activeTabId, handleTabClick, handleCreateTab, handleDeleteTab } = usePageTabs<ReleaseFiltersType>({
    scope: 'wantlist',
    filters,
    setFilters,
    setSearch,
    defaultFilters: { ...DEFAULT_FILTERS, sort: globalSort, order: globalOrder },
  })

  // Sync global sort only when on default tab
  useEffect(() => {
    if (activeTabId === null) setFilters(prev => ({ ...prev, sort: globalSort, order: globalOrder }))
  }, [globalSort, globalOrder, activeTabId])

  // When sort is 'date_added', paginate IDs client-side; otherwise pass all IDs and let server sort.
  const isDateSort = filters.sort === 'date_added'
  const perPage = filters.per_page ?? 50
  const currentPage = filters.page ?? 1

  const { pageIds, dateTotal } = useMemo(() => {
    if (!isDateSort) return { pageIds: null, dateTotal: 0 }
    const sorted = [...wantlistIds].sort((a, b) => {
      const da = wantlistDates[a] ?? ''
      const db = wantlistDates[b] ?? ''
      return filters.order === 'asc' ? da.localeCompare(db) : db.localeCompare(da)
    })
    const start = (currentPage - 1) * perPage
    return { pageIds: sorted.slice(start, start + perPage), dateTotal: sorted.length }
  }, [isDateSort, wantlistIds, wantlistDates, filters.order, currentPage, perPage])

  // Build final filters with wantlist IDs
  const queryFilters = useMemo<ReleaseFiltersType>(() => {
    if (isDateSort) {
      return { ...filters, release_ids: pageIds ?? [], sort: 'elevated_score', order: 'desc', page: 1, per_page: perPage }
    }
    return { ...filters, release_ids: wantlistIds }
  }, [filters, wantlistIds, isDateSort, pageIds, perPage])

  const { data, isLoading, error } = useReleases(queryFilters, {
    enabled: isAuthenticated && wantlistIds.length > 0,
  })

  // Re-sort returned releases by date_added client-side (server doesn't know the order)
  const displayReleases = useMemo(() => {
    if (!isDateSort || !data?.releases) return data?.releases
    const dir = filters.order === 'asc' ? 1 : -1
    return [...data.releases].sort((a, b) => {
      const da = wantlistDates[a.id] ?? ''
      const db = wantlistDates[b.id] ?? ''
      return da < db ? -dir : da > db ? dir : 0
    })
  }, [data?.releases, isDateSort, wantlistDates, filters.order])

  const paginationInfo = isDateSort ? {
    page: currentPage,
    pages: Math.ceil(dateTotal / perPage),
    has_next: currentPage * perPage < dateTotal,
    has_prev: currentPage > 1,
  } : data?.pagination ? {
    page: data.pagination.page,
    pages: data.pagination.total_pages,
    has_next: data.pagination.page < data.pagination.total_pages,
    has_prev: data.pagination.page > 1,
  } : undefined

  const handleSearch = useCallback((value: string) => {
    setSearch(value)
    setFilters(prev => ({ ...prev, q: value || undefined, page: 1 }))
  }, [])

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleSync = async () => {
    if (!isAuthenticated || !user) { showToast('Please login to Discogs first', 'warning'); return }
    if (syncProgress.type) { showToast('Already syncing, please wait', 'warning'); return }
    try {
      await fetchWantlist()
      showToast('Wantlist synced successfully', 'success')
    } catch {
      showToast('Failed to sync wantlist', 'error')
    }
  }

  if (!isAuthenticated) {
    return (
      <div className={styles.page}>
        <div className={styles.pageHeader}><h1>Wantlist</h1></div>
        <div className={styles.empty}>
          <i className="bi bi-vinyl"></i>
          <div className={styles.emptyTitle}>Connect your Discogs account</div>
          <p>Sync your wantlist and get alerts when wanted records get listed.</p>
          <Button
            variant="primary"
            icon={`bi ${isLoggingIn ? 'bi-arrow-repeat' : 'bi-vinyl'}`}
            onClick={login}
            disabled={isLoggingIn}
          >
            {isLoggingIn ? 'Connecting…' : 'Connect Discogs'}
          </Button>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.pageHeader}><h1>Wantlist</h1></div>
        <div className={styles.error}>
          <i className="bi bi-exclamation-triangle"></i>
          <p>Failed to load wantlist releases</p>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h1>Wantlist</h1>
          <DiscogsSyncBadge list="wantlist" />
          <Button variant="accentSoft" icon={`bi ${isSyncing ? 'bi-arrow-repeat' : 'bi-arrow-clockwise'}`} onClick={handleSync} disabled={isSyncing || !isAuthenticated} title="Sync wantlist from Discogs">
            {isSyncing
              ? syncProgress.total > 0
                ? `Syncing page ${syncProgress.current}/${syncProgress.total} (${syncProgress.totalItems.toLocaleString()} items)...`
                : 'Starting sync...'
              : 'Sync'}
          </Button>
        </div>
      </div>

      <PageTabBar
        tabs={tabs}
        activeTabId={activeTabId}
        defaultLabel="All Wantlist"
        onTabClick={handleTabClick}
        onCreateTab={handleCreateTab}
        onDeleteTab={handleDeleteTab}
      />

      <ReleaseToolbar
        search={search}
        onSearchChange={setSearch}
        onSearchSubmit={handleSearch}
        searchPlaceholder="Search wantlist..."
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        filters={filters}
        onFiltersChange={update => setFilters(prev => ({ ...prev, ...update }))}
        viewSortProps={{
          extraOptions: [{ value: 'date_added', label: 'Date Added' }],
          // When on a named tab, sort belongs to the tab (saved as part of its
          // filters) rather than the shared global default.
          ...(activeTabId !== null ? {
            sort: filters.sort,
            order: filters.order,
            onSortChange: (sort: string) =>
              setFilters(prev => ({ ...prev, sort, order: SORT_DEFAULT_ASC.has(sort) ? 'asc' as const : 'desc' as const, page: 1 })),
            onToggleOrder: () =>
              setFilters(prev => ({ ...prev, order: prev.order === 'desc' ? 'asc' as const : 'desc' as const })),
          } : {}),
        }}
      />

      {showFilters && <ReleaseFilters filters={filters} onFiltersChange={setFilters} onClose={() => setShowFilters(false)} contextReleaseIds={wantlistIds} />}

      {wantlistIds.length === 0 && !isSyncing ? (
        <div className={styles.empty}>
          <i className="bi bi-heart"></i>
          <p>Your wantlist hasn't been synced yet</p>
          <Button variant="primary" icon="bi bi-arrow-clockwise" onClick={handleSync}>
            Sync Wantlist
          </Button>
        </div>
      ) : isLoading ? (
        <LoadingState message="Loading wantlist..." />
      ) : (
        <div className={releaseStyles.releasesLayout}>
          <div className={releaseStyles.tableContainer}>
            <ReleaseListView
              releases={displayReleases ?? []}
              viewMode={viewMode}
              emptyIcon="bi bi-heart"
              emptyMessage="No matching releases"
              emptyHint="Try adjusting your filters"
            />

            {paginationInfo && (
              <Pagination
                page={paginationInfo.page}
                totalPages={paginationInfo.pages}
                hasNext={paginationInfo.has_next}
                hasPrev={paginationInfo.has_prev}
                onPageChange={handlePageChange}
                perPage={filters.per_page}
                onPerPageChange={(per_page) => setFilters(prev => ({ ...prev, per_page, page: 1 }))}
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}
